import React, { useState, useEffect } from 'react';
import { auditAPI, utentiAPI } from '../services/api';
import '../styles/RegistroAccessi.css';

/**
 * RegistroAccessi — schermata riservata agli admin.
 *
 * Risponde a due domande:
 *   "chi ha consultato la cartella di questo paziente?"
 *   "cosa ha consultato questo utente?"
 *
 * La prima serve quando un paziente chiede di sapere chi ha trattato
 * i suoi dati; la seconda per accorgersi di consultazioni anomale.
 */

const messaggioErrore = (e, ripiego) => e?.message || e?.response?.data?.message || ripiego;

const dataOra = (v) =>
    v ? new Date(v).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const ETICHETTE_AZIONE = {
    apertura_scheda: 'Apertura scheda',
    consultazione_file: 'Apertura documento',
    esportazione: 'Esportazione dati',
    ricerca: 'Ricerca',
};

const FILTRI_VUOTI = { paziente_id: '', utente_id: '', azione: '', dal: '', al: '' };

const RegistroAccessi = () => {
    const [accessi, setAccessi] = useState([]);
    const [riepilogo, setRiepilogo] = useState(null);
    const [utenti, setUtenti] = useState([]);
    const [filtri, setFiltri] = useState(FILTRI_VUOTI);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);

    const carica = async (f = filtri) => {
        try {
            setCaricamento(true);
            setErrore(null);
            const puliti = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''));
            const r = await auditAPI.accessi(puliti);
            setAccessi(r.data?.data?.accessi || []);
        } catch (e) {
            setErrore(messaggioErrore(e, 'Impossibile caricare il registro'));
        } finally {
            setCaricamento(false);
        }
    };

    useEffect(() => {
        carica(FILTRI_VUOTI);
        auditAPI.riepilogo()
            .then((r) => setRiepilogo(r.data?.data || null))
            .catch(() => {});
        utentiAPI.getAll()
            .then((r) => setUtenti(r.data?.data?.utenti || []))
            .catch(() => {});
        // eslint-disable-next-line
    }, []);

    const aggiorna = (campo, valore) => setFiltri((p) => ({ ...p, [campo]: valore }));

    const azzera = () => {
        setFiltri(FILTRI_VUOTI);
        carica(FILTRI_VUOTI);
    };

    const etichetta = (a) => ETICHETTE_AZIONE[a] || a;

    return (
        <div className="ra-wrap">
            <div className="ra-testa">
                <div>
                    <h1>Registro accessi</h1>
                    <p className="ra-sottotitolo">
                        Chi ha consultato quali dati clinici. Le consultazioni ravvicinate
                        dello stesso dato sono raggruppate in finestre di quindici minuti.
                    </p>
                </div>
            </div>

            {riepilogo && (
                <div className="ra-riepilogo">
                    <div className="ra-scheda">
                        <span className="ra-scheda-titolo">Ultimi 30 giorni</span>
                        <div className="ra-conteggi">
                            {riepilogo.per_azione.map((r) => (
                                <div key={r.azione} className="ra-conteggio">
                                    <strong>{r.n}</strong>
                                    <span>{etichetta(r.azione)}</span>
                                </div>
                            ))}
                            {riepilogo.per_azione.length === 0 && (
                                <span className="ra-vuoto-inline">Nessun accesso registrato.</span>
                            )}
                        </div>
                    </div>

                    {riepilogo.per_utente.length > 0 && (
                        <div className="ra-scheda">
                            <span className="ra-scheda-titolo">Per utente</span>
                            <div className="ra-utenti">
                                {riepilogo.per_utente.slice(0, 6).map((u) => (
                                    <div key={u.utente_id || 'ignoto'} className="ra-utente">
                                        <span className="ra-utente-nome">{u.utente_nome || 'non registrato'}</span>
                                        <span className="ra-utente-dati">
                                            {u.n} accessi · {u.pazienti_distinti} pazienti
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="ra-filtri">
                <div className="ra-campo">
                    <label htmlFor="utente_id">Utente</label>
                    <select
                        id="utente_id"
                        value={filtri.utente_id}
                        onChange={(e) => aggiorna('utente_id', e.target.value)}
                    >
                        <option value="">tutti</option>
                        {utenti.map((u) => (
                            <option key={u.id} value={u.id}>{u.cognome} {u.nome}</option>
                        ))}
                    </select>
                </div>

                <div className="ra-campo">
                    <label htmlFor="azione">Tipo di accesso</label>
                    <select
                        id="azione"
                        value={filtri.azione}
                        onChange={(e) => aggiorna('azione', e.target.value)}
                    >
                        <option value="">tutti</option>
                        {Object.entries(ETICHETTE_AZIONE).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                </div>

                <div className="ra-campo">
                    <label htmlFor="paziente_id">Id paziente</label>
                    <input
                        id="paziente_id"
                        type="number"
                        value={filtri.paziente_id}
                        onChange={(e) => aggiorna('paziente_id', e.target.value)}
                        placeholder="es. 11"
                    />
                </div>

                <div className="ra-campo">
                    <label htmlFor="dal">Dal</label>
                    <input id="dal" type="date" value={filtri.dal}
                           onChange={(e) => aggiorna('dal', e.target.value)} />
                </div>

                <div className="ra-campo">
                    <label htmlFor="al">Al</label>
                    <input id="al" type="date" value={filtri.al}
                           onChange={(e) => aggiorna('al', e.target.value)} />
                </div>

                <div className="ra-filtri-azioni">
                    <button className="ra-btn-secondario" onClick={azzera}>Azzera</button>
                    <button className="ra-btn-primario" onClick={() => carica()} disabled={caricamento}>
                        {caricamento ? 'Ricerca…' : 'Cerca'}
                    </button>
                </div>
            </div>

            {errore && <div className="ra-errore">{errore}</div>}

            {caricamento ? (
                <div className="ra-stato">Caricamento…</div>
            ) : accessi.length === 0 ? (
                <div className="ra-vuoto">Nessun accesso corrisponde ai filtri.</div>
            ) : (
                <table className="ra-tabella">
                    <thead>
                        <tr>
                            <th>Quando</th>
                            <th>Utente</th>
                            <th>Azione</th>
                            <th>Paziente</th>
                            <th>Dettaglio</th>
                            <th>Provenienza</th>
                        </tr>
                    </thead>
                    <tbody>
                        {accessi.map((a) => (
                            <tr key={a.id} className={a.azione === 'esportazione' ? 'ra-riga-rilievo' : ''}>
                                <td className="ra-quando">{dataOra(a.created_at)}</td>
                                <td>
                                    {a.utente_nome || <span className="ra-ignoto">non registrato</span>}
                                    {a.utente_ruolo && <div className="ra-ruolo">{a.utente_ruolo}</div>}
                                </td>
                                <td><span className={`ra-azione ra-azione-${a.azione}`}>{etichetta(a.azione)}</span></td>
                                <td>
                                    {a.paziente_id ? (
                                        <>
                                            {a.paziente_cognome} {a.paziente_nome}
                                            <div className="ra-pid">{a.id_paziente || `#${a.paziente_id}`}</div>
                                        </>
                                    ) : '—'}
                                </td>
                                <td className="ra-dettaglio">{a.dettaglio || '—'}</td>
                                <td className="ra-ip">{a.ip || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <p className="ra-piede">
                Il registro mostra le ultime 100 righe corrispondenti ai filtri. I dati
                di accesso sono a loro volta informazioni personali e vanno conservati
                per un periodo definito, non indefinitamente.
            </p>
        </div>
    );
};

export default RegistroAccessi;
